import React, { useState, useEffect, useMemo } from 'react';
import { SketchPicker } from 'react-color'
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import InputBase from '@material-ui/core/InputBase';
import Popover from '@material-ui/core/Popover';
import SvgIcon from '@material-ui/core/SvgIcon';
import IconButton from '@material-ui/core/IconButton';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';

import * as Icons from '@material-ui/icons'

import AlignLeft from '@material-ui/icons/FormatAlignLeft';
import AlignCenter from '@material-ui/icons/FormatAlignCenter';
import AlignRight from '@material-ui/icons/FormatAlignRight';
import DeleteIcon from '@material-ui/icons/Delete';

import SelectFileDialog from 'app/components/drive/SelectFileDialog';
import FileTypes from 'app/utils/FileTypes';
import { Typography } from '@material-ui/core';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    container: {
        position: 'relative'
    },
    liContainer: {
        border: `1px solid rgba(0, 0, 0, 0.23)`,
        borderRadius: theme.shape.borderRadius,
    },
    liSecondaryAction: {
        right: 3
    },
    gridContainer: {
        marginBottom: 0,
        marginTop: 0
    },
    toolbar: {
        padding: theme.spacing(1),
        backgroundColor: '#fff',
        borderTop: `1px solid #ddd`,
        borderBottom: `1px solid #ddd`
    },
    active: {
        backgroundColor: 'rgba(0, 0, 0, 0.10);',
        "&:hover" : {
            backgroundColor: 'background-color: rgba(0, 0, 0, 0.10);',
        }
    },
    buttonGroupBorder: {
        border: `1px solid rgba(0, 0, 0, 0.23)`,
        borderRadius: theme.shape.borderRadius,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingTop: 1,
        paddingBottom: 1
    },
    input: {
        minWidth: 'auto',

        '& input[type=number]': {
            textAlign: 'center',
            width: 30,
            '-webkit-appearance': 'none',
            margin: 0
        },
        '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        }
    },
    btn: {
        padding: '4px 9px'
    },
    checkboxLabel: {
        fontSize: '11px'
    }
}));

function FileListItem(props) {

    const classes = useStyles();

    const { file, onRemove } = props;

    const Icon = useMemo(() => {
        if ('extension' in file) {
            const fileType = FileTypes.find(obj => obj.ext === file.extension);
            return Icons[fileType?.icon ?? 'Description'];
        } else return Icons['Description'];

    }, [file]);

    return (
        <ListItem 
            ContainerComponent = 'div' 
            dense 
            classes={{container: classes.liContainer}}
            >
            <ListItemIcon>
                <Icon />
            </ListItemIcon>
            <ListItemText primary={file.name}/>
            <ListItemSecondaryAction className={classes.liSecondaryAction}>
                <IconButton aria-label="Remove File" onClick={onRemove}>
                    <DeleteIcon fontSize="small"/>
                </IconButton>
            </ListItemSecondaryAction>
        </ListItem>
    )
}

export default function RichLinkEditor(props) {

    const { options, active, handleOptions, includeEmailNotice } = props;

    const {
        background_color,
        link_type,
        content,
        padding_top,
        padding_left,
        padding_right,
        padding_bottom,
        button_text,
        button_text_color,
        button_color,
        button_radius,
        button_align,
        button_padding_width,
        button_padding_height,
        target
    } = options;

    const api = useApi();

    const [ colorPickerAnchorEl, setColorPickerAnchorEl ] = useState(null);
    const [ colorPickerKey, setColorPickerKey ] = useState(null);
    const [ selectFileDialogOpen, setSelectFileDialogOpen ] = useState(false);

    const [ pages, setPages ] = useState([]);
    const [ pagesLoading, setPagesLoading ] = useState(false);

    const classes = useStyles();

    const getPages = () => {
        setPagesLoading(true);

        api.get(`/admin/page`)
            .then(res => setPages(res.pages))
            .finally(() => setPagesLoading(false));
    }

    useEffect(() => {
        getPages();
    }, []);

    const handleNumber = (e, name) => {
        handleOptions(name, parseInt(e.target.value) || 0);
    }

    const handleColor = (color) => {
        if (color && color.hex) {
            handleOptions(colorPickerKey, color.hex);
        }
    }

    const handleTarget= () => {
        handleOptions('target', !target);
    }
    const handleLinkType = e => {
        handleOptions('content', "")
        handleOptions('link_type', e.target.value)
    }

    // Color Picker Popover....................

    const openColorPicker = (event, key) => {
        event.preventDefault();
        setColorPickerAnchorEl(event.currentTarget);
        setColorPickerKey(key);
    }

    const closeColorPicker= () => {
        setColorPickerAnchorEl(null);
        setColorPickerKey(null);
    }

    // File Dialog ----------------------------

    const openSelectFileDialog = () => {
        setSelectFileDialogOpen(true);
    }

    const closeSelectFileDialog = () => {
        setSelectFileDialogOpen(false);
    }

    const closeSelectFileDialogAndHandleFile = (file) => {
        closeSelectFileDialog();
        handleOptions('content', file)
    }

    // Constants -----------------

    const selectAll = (e) => e.target.select();

    return (
        <div className={classes.container}>
            <Collapse in={active}>
                <div className={classes.toolbar}>
                    <Grid container alignItems="center" spacing={1} wrap="nowrap">
                        <Grid item>
                            <Typography variant="subtitle2">
                                Link Options:
                            </Typography>
                        </Grid>
                        <Grid item>
                            <TextField
                                select
                                value={link_type}
                                onChange={handleLinkType}
                                variant="outlined"
                                size="small"
                                >
                                <MenuItem value='link' dense>
                                    Standard Link
                                </MenuItem>
                                <MenuItem value='drive' dense>
                                    From Drive
                                </MenuItem>
                                {!includeEmailNotice &&
                                    <MenuItem value='page' dense>
                                        Page Link
                                    </MenuItem>
                                }
                            </TextField>
                        </Grid>
                        {(link_type === 'drive') &&
                            <Grid item>
                                {(content && typeof content === 'object') ? (
                                    <FileListItem
                                        file={content}
                                        onRemove={() => handleOptions('content', "")}
                                    />
                                ) : (
                                    <Button
                                        variant='outlined'
                                        onClick={openSelectFileDialog}
                                        >
                                        Add File
                                    </Button>
                                )}
                            </Grid>
                        }
                        {(link_type === 'link') &&
                            <>
                                <Grid item>
                                    <TextField
                                        value={content}
                                        onChange={(e) => handleOptions('content', e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        placeholder="https://example.com"
                                        label="Full URL"
                                        >
                                    </TextField>
                                </Grid>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={target}
                                                onChange={handleTarget}
                                                value="target"
                                                size="small"
                                            />
                                        }
                                        label="Open new tab?"
                                        disabled={includeEmailNotice}
                                        className={classes.checkboxLabel}
                                    />
                                </Grid>
                            </>
                        }
                        {(link_type === 'page' && pages.length) &&
                            <Grid item xs={9}>
                                <TextField
                                    select
                                    value={content}
                                    onChange={(e) => handleOptions('content', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    disabled={pagesLoading}
                                    fullWidth
                                    >
                                    <MenuItem value={""} dense key={0}>
                                        Please select a page...
                                    </MenuItem>
                                    {pages.map(p => (
                                        <MenuItem value={p.path} dense key={p.id}>
                                            {p.title}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        }
                    </Grid>
                    {(includeEmailNotice && link_type === 'drive') &&
                        <Grid container alignItems="center" spacing={1} wrap="nowrap">
                            <Grid item>
                                <Typography variant="caption" color="error">
                                    *Please Note: Linked drive files will only be accessable for 7 days due to security.
                                </Typography>
                            </Grid>
                        </Grid>
                    }
                    <Grid container alignItems="center" spacing={1} wrap="nowrap">
                        <Grid item>
                            <Typography variant="subtitle2">
                                Button Options:
                            </Typography>
                        </Grid>
                        <Grid item>
                            <TextField
                                value={button_text}
                                onChange={(e) => handleOptions('button_text', e.target.value)}
                                variant="outlined"
                                size="small"
                                >
                            </TextField>
                        </Grid>
                        <Grid item>
                            <div className={classes.buttonGroupBorder}>
                                <Tooltip title="Button Width">
                                    <InputBase
                                        value={button_padding_width}
                                        onChange={(e) => handleNumber(e, 'button_padding_width')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                                <Tooltip title="Button Height">
                                    <InputBase
                                        value={button_padding_height}
                                        onChange={(e) => handleNumber(e, 'button_padding_height')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                                <Tooltip title="Button Radius">
                                    <InputBase
                                        value={button_radius}
                                        onChange={(e) => handleNumber(e, 'button_radius')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                            </div>
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title="Background Color">
                                    <Button className={classes.btn} aria-label="Background Color" size="small" onClick={event => openColorPicker(event, 'button_color')}>
                                        <SvgIcon>
                                            <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15c-.59.59-.59 1.54 0 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z"/>
                                            <path style={{ color: button_color }} d="M0 20h24v4H0z"/>
                                        </SvgIcon>
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title="Text Color">
                                    <Button className={classes.btn} aria-label="Text Color" size="small" onClick={event => openColorPicker(event, 'button_text_color')}>
                                        <SvgIcon>
                                            <path d="M0 20h24v4H0z" style={{ color: button_text_color }}/>
                                            <path d="M11 3L5.5 17h2.25l1.12-3h6.25l1.12 3h2.25L13 3h-2zm-1.38 9L12 5.67 14.38 12H9.62z"/>
                                        </SvgIcon>
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item>
                            <Typography variant="subtitle2">
                                Container Options:
                            </Typography>
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title='Align Button Left'>
                                    <Button
                                        size="small"
                                        className={button_align === 'left' ? classes.active : null}
                                        onClick={() => handleOptions('button_align', 'left')}
                                        >
                                        <AlignLeft />
                                    </Button>
                                </Tooltip>
                                <Tooltip title='Align Button Center'>
                                    <Button
                                        size="small"
                                        className={button_align === 'center' ? classes.active : null}
                                        onClick={() => handleOptions('button_align', 'center')}
                                        >
                                        <AlignCenter />
                                    </Button>
                                </Tooltip>
                                <Tooltip title='Align Button Right'>
                                    <Button
                                        size="small"
                                        className={button_align === 'right' ? classes.active : null}
                                        onClick={() => handleOptions('button_align', 'right')}
                                        >
                                        <AlignRight />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                        <Grid item>
                            <div className={classes.buttonGroupBorder}>
                                <Tooltip title="Padding Top">
                                    <InputBase
                                        value={padding_top}
                                        onChange={(e) => handleNumber(e, 'padding_top')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Bottom">
                                    <InputBase
                                        value={padding_bottom}
                                        onChange={(e) => handleNumber(e, 'padding_bottom')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Left">
                                    <InputBase
                                        value={padding_left}
                                        onChange={(e) => handleNumber(e, 'padding_left')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Right">
                                    <InputBase
                                        value={padding_right}
                                        onChange={(e) => handleNumber(e, 'padding_right')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                            </div>
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title="Background Color">
                                    <Button className={classes.btn} aria-label="Background Color" size="small" onClick={event => openColorPicker(event, 'background_color')}>
                                        <SvgIcon>
                                            <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15c-.59.59-.59 1.54 0 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z"/>
                                            <path style={{ color: background_color }} d="M0 20h24v4H0z"/>
                                        </SvgIcon>
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </div>
            </Collapse>
            <div className={classes.root} onClick={focus} style={{
                backgroundColor: background_color,
                paddingTop: padding_top,
                paddingBottom: padding_bottom,
                paddingLeft: padding_left,
                paddingRight: padding_right,
                textAlign: button_align
                }}
                >
                <div style={{
                    display: 'inline-block',
                    cursor: 'pointer',
                    color: button_text_color,
                    backgroundColor: button_color,
                    borderRadius: button_radius,
                    paddingTop: button_padding_height,
                    paddingBottom: button_padding_height,
                    paddingLeft: button_padding_width,
                    paddingRight: button_padding_width,
                    }}
                    >
                    <strong>{button_text}</strong>
                </div>
            </div>
            <SelectFileDialog
                open={selectFileDialogOpen}
                onSelect={closeSelectFileDialogAndHandleFile}
                onClose={closeSelectFileDialog}
            />
            <Popover
                open={Boolean(colorPickerAnchorEl)}
                anchorEl={colorPickerAnchorEl}
                onClose={closeColorPicker}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                >
                <SketchPicker color={options[colorPickerKey]} onChange={handleColor} disableAlpha />
            </Popover>
        </div>
    )
}
